import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import useConversations from 'src/hooks/useDPXConversations';
import useLead from 'src/hooks/useLead';
import { IConversation } from 'src/interfaces/conversation';
import { ILead } from 'src/interfaces/lead';
import { BDC_LEAD_ROUTE, DIGITAL_LEAD_ROUTE } from 'src/constants/lead/routes';
import { getTemplateArea } from 'src/components/Lead/WhatsApp/utils/getTemplateArea';
import { formatRedirectUrlLead } from 'src/modules/conversations/helpers/formatRedirectUrlLead';
import { getDocumentId } from 'src/utils/mongo/getDocumentId';
import { CONVERSATIONS_PATH } from '../constants';
import { TSelectedLead } from '../types';

const CONVERSATION_ID_PARAM = 'id';
const LEAD_DETAIL_PATH = new RegExp(
    `^/management/(?:${DIGITAL_LEAD_ROUTE}|${BDC_LEAD_ROUTE})/([a-f\\d]{24})(?:/|$)`,
    'i'
);

/**
 * Lead the assistant can talk about: the one in the lead detail route, or the
 * lead of the conversation open in /conversations.
 */
export const useAssistantLeadSelection = () => {
    const { pathname, search } = useLocation();
    const { conversation } = useConversations() as {
        conversation: IConversation | null;
    };
    const { lead } = useLead() as { lead: ILead | null };

    const routeLeadId = pathname.match(LEAD_DETAIL_PATH)?.[1] || null;
    const conversationId = new URLSearchParams(search).get(
        CONVERSATION_ID_PARAM
    );
    const conversationLeadId =
        pathname === CONVERSATIONS_PATH &&
        conversationId &&
        conversation?._id === conversationId
            ? getDocumentId(conversation.lead) || null
            : null;
    const availableLeadId = routeLeadId || conversationLeadId;

    const selectedLead = useMemo((): TSelectedLead | null => {
        if (!availableLeadId) return null;
        const fromConversation = Boolean(conversationLeadId);
        const loadedLead = lead?._id === availableLeadId ? lead : null;
        return {
            id: availableLeadId,
            name: fromConversation
                ? conversation.name
                : loadedLead?.name || null,
            route: fromConversation
                ? formatRedirectUrlLead(conversation)
                : pathname,
            conversationId: fromConversation
                ? conversationId
                : getDocumentId(loadedLead?.conversation) || null,
            conversationArea: fromConversation
                ? conversation.area
                : loadedLead
                  ? getTemplateArea(loadedLead)
                  : null,
            store: fromConversation
                ? conversation.store
                : loadedLead?.store || null,
            updatedAt: loadedLead?.updatedAt || null,
        };
    }, [
        availableLeadId,
        conversation,
        conversationId,
        conversationLeadId,
        lead,
        pathname,
    ]);

    return { availableLeadId, selectedLead };
};
